import React, { useState, useEffect } from 'react';
import { Box, Typography, Stepper, Step, StepLabel, StepContent, Button } from "@mui/material";
import FormField from "../../components/FormComponent/FormField/FormField";
import styles from "./styles/styles.module.css";
import { clinicRegistration } from '../../JsonForms/ClinicRegistration';
import { axiosProxyInstance } from './../../utils/request';
import { useForm } from 'react-hook-form';
import cogoToast from "cogo-toast";
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { useJsApiLoader } from '@react-google-maps/api';
import ErrorIcon from '@mui/icons-material/Error';

const phoneRegExp = /^[0-9]\d*$/;

const validationSchema = yup.object({
  clinicName: yup
    .string()
    .matches(/^[a-zA-Z0-9\s,.&()-]+$/, "Clinic name cannot contain special characters")
    .max(100, "Clinic name cannot exceed 100 characters")
    .required("Clinic name is required")
    .test(
      "not-only-spaces",
      "This field cannot contain only blank spaces",
      (value) => value ? value.trim().length > 0 : true // Ensure non-blank content
    ),
  streetAddress: yup.string()
    .required('Street address is required')
    .test(
      "not-only-spaces",
      "This field cannot contain only blank spaces",
      (value) => value ? value.trim().length > 0 : true // Ensure non-blank content
    ),
  buildingName: yup.string()
    .test(
      "not-only-spaces",
      "This field cannot contain only blank spaces",
      (value) => value ? value.trim().length > 0 : true // Ensure non-blank content
    ),
  town: yup
    .object()
    .transform((value, originalValue) => {
      return typeof originalValue === 'string' ? undefined : value;
    })
    .required('Town/City is required'),
  postcode: yup.string()
    .required('Postcode is required')
    .matches(/^[a-zA-Z0-9\s,.&()-]+$/, "Postcode cannot contain special characters")
    .test(
      "not-only-spaces",
      "This field cannot contain only blank spaces",
      (value) => value ? value.trim().length > 0 : true // Ensure non-blank content
    ),
  phone: yup
    .string()
    .required('Phone number is required')
    .matches(phoneRegExp, "Phone number is not valid"),
  physiotherapistsCount: yup.string().required('Please select number of physiotherapists'),
  areaOfSpeciality: yup.array().min(1, 'At least one speciality is required').required('At least one speciality is required'),
  additionalServices: yup
    .array()
    .transform((value, originalValue) =>
      typeof originalValue === 'string' ? originalValue.split(',') : originalValue
    )
    .of(yup.string()),
  software: yup.string()
    .test(
      "not-only-spaces",
      "This field cannot contain only blank spaces",
      (value) => value ? value.trim().length > 0 : true // Ensure non-blank content
    ),
});

const updateValidationSchema = yup.object({
  clinicName: yup
    .string()
    .matches(/^[a-zA-Z0-9\s,.&()-]+$/, "Clinic name cannot contain special characters")
    .max(100, "Clinic name cannot exceed 100 characters")
    .required("Clinic name is required")
    .test(
      "not-only-spaces",
      "This field cannot contain only blank spaces",
      (value) => value ? value.trim().length > 0 : true // Ensure non-blank content
    ),
  streetAddress: yup.string()
    .required('Street address is required')
    .test(
      "not-only-spaces",
      "This field cannot contain only blank spaces",
      (value) => value ? value.trim().length > 0 : true // Ensure non-blank content
    ),
  buildingName: yup.string()
    .test(
      "not-only-spaces",
      "This field cannot contain only blank spaces",
      (value) => value ? value.trim().length > 0 : true // Ensure non-blank content
    ),
  town: yup
    .mixed()
    .transform((value, originalValue) => {
      if (typeof originalValue === 'string') {
        return originalValue.trim() === '' ? undefined : originalValue;
      }
      return value;
    })
    .test(
      'is-valid-town',
      'Town/City is required',
      (value) => {
        // Accept either a non-empty string or an object with required props
        if (typeof value === 'string') {
          return value.trim().length > 0;
        }
        if (typeof value === 'object' && value !== null) {
          return 'name' in value || 'id' in value; // adjust as needed
        }
        return false;
      }
    )
    .required('Town/City is required'),
  postcode: yup.string()
    .required('Postcode is required')
    .matches(/^[a-zA-Z0-9\s,.&()-]+$/, "Postcode cannot contain special characters")
    .test(
      "not-only-spaces",
      "This field cannot contain only blank spaces",
      (value) => value ? value.trim().length > 0 : true // Ensure non-blank content
    ),
  phone: yup
    .string()
    .required('Phone number is required')
    .matches(phoneRegExp, "Phone number is not valid"),
  physiotherapistsCount: yup.string().required('Please select number of physiotherapists'),
  areaOfSpeciality: yup.array().min(1, 'At least one speciality is required').required('At least one speciality is required'),
  additionalServices: yup
    .array()
    .transform((value, originalValue) =>
      typeof originalValue === 'string' ? originalValue.split(',') : originalValue
    )
    .of(yup.string()),
  software: yup.string()
    .test(
      "not-only-spaces",
      "This field cannot contain only blank spaces",
      (value) => value ? value.trim().length > 0 : true // Ensure non-blank content
    ),
});

const ClinicRegistration = ({ goToStep, clinicRegistrationData, setClinicRegistrationData }) => {
  const { isLoaded } = useJsApiLoader({
      googleMapsApiKey: process.env.REACT_APP_MAP_KEY,
      libraries: ['places'],
  });
  const [options, setOptions] = useState(null);
  const [selectedValues, setSelectedValues] = useState({});
  let clinicId = localStorage.getItem("LPCurrentClinicId");

  const { register, handleSubmit, control, formState: { errors }, reset } = useForm({
    resolver: yupResolver(clinicId?updateValidationSchema:validationSchema),
    mode: "onBlur",
  });

  useEffect(() => {
    if(clinicRegistrationData){
      const resetValueObject = {
        clinicName: clinicRegistrationData?.title || "",
        streetAddress: clinicRegistrationData?.address_1 || "",
        buildingName: clinicRegistrationData?.address_2 || "",
        town: clinicRegistrationData?.town || null,
        physiotherapistsCount: clinicRegistrationData?.num_workers || "",
        postcode: clinicRegistrationData?.postcode || "",
        phone: clinicRegistrationData?.telephone_1 || "",
        software: clinicRegistrationData?.booking_management_software || "",
        areaOfSpeciality: Object.keys(clinicRegistrationData?.selectedSpecialization || {})?.length ? Object.keys(clinicRegistrationData?.selectedSpecialization || {}).map(key => "on") : []
      };
      
      setSelectedValues(clinicRegistrationData?.selectedSpecialization);
      reset(resetValueObject);
    }
  }, [clinicRegistrationData]);

  const geocodeAddress = (address) => {
    return new Promise((resolve, reject) => {
        const geocoder = new window.google.maps.Geocoder();

        geocoder.geocode({ address }, (results, status) => {
        if (status === 'OK' && results[0]) {
            const location = results[0].geometry.location;
            resolve({ lat: location.lat(), lng: location.lng() });
        } else {
            console.error('Geocode error:', status);
            reject(status);
        }
        });
    });
  };

  const handleLinkButtonClick = async (field, parentFieldGroup) => {
    if (parentFieldGroup.action === "searchCity") {
      try {
        const getLocationsResponse = await axiosProxyInstance.get(`/location?town=${field}`);
        const responseData = getLocationsResponse.data?.data;
        const optionPromises = responseData?.map(async (item) => {
          const fullAddress = `${item.n}, ${item.c}, ${item.r}, ${item.cc}`;
          try {
              const coords = await geocodeAddress(fullAddress);
              return {
                  label: item.n,
                  value: item.id,
                  lat: coords.lat,
                  lng: coords.lng,
              };
          } catch (err) {
              return {
                  label: item.n,
                  value: item.id,
                  lat: null,
                  lng: null,
              };
          }
          // return { label: item.n, value: item?.id };
        });
        const options = await Promise.all(optionPromises);
        setOptions(options);
      } catch (error) {
        console.error('Error during signup:', error);
      } finally {
      }
    }
  }

  const onSubmit = async (data) => {
    const currentUserId = localStorage.getItem("LPCurrentUserId");
    if (currentUserId) {
      try {
        const combinedAdditionalServices = Object.values(selectedValues).flat();
        const combinedSpecialization = Object.keys(selectedValues);
        const apiData = {
          title: data.clinicName,
          address_1: data.streetAddress,
          address_2: data.buildingName || '',
          location: typeof data.town === 'string' ? [data.town] : [data?.town?.value] || '',
          num_workers: data.physiotherapistsCount,
          postcode: data.postcode,
          specialization: combinedSpecialization || [],
          telephone_1: data.phone,
          treatments: combinedAdditionalServices || [],
          provideruid: currentUserId,
          uid: currentUserId,
          latitude: typeof data.town === 'string' ? '' : data?.town?.lat || '',
          longitude: typeof data.town === 'string' ? '' : data?.town?.lng || '',
          booking_management_software: data.software,
          
          town: data.town,
          selectedSpecialization: selectedValues
        };

        const email = localStorage.getItem("LPCurrentUserEmail");
        let newApiData = {
          btn_submit: true,
          provideruid: currentUserId,
          uid: currentUserId,
          listings: [
            {
              title: data.clinicName,
              email: email,
              address_1: data.streetAddress,
              address_2: data.buildingName || '',
              location: data.town?.value,
              num_workers: data.physiotherapistsCount,
              postcode: data.postcode,
              specialization: combinedSpecialization || [],
              telephone_1: data.phone,
              treatments: combinedAdditionalServices || [],
              latitude: typeof data.town === 'string' ? '' : data?.town?.lat || '',
              longitude: typeof data.town === 'string' ? '' : data?.town?.lng || '',
              booking_management_software: data.software,
            }
          ],
        };
        if(clinicId){
          newApiData.listings[0].listing_id = clinicId;
          newApiData.listings[0].provideruid = currentUserId;
          newApiData.listings[0].uid = currentUserId;
        }else{}
        const response = await axiosProxyInstance.post('/signup/2/', newApiData);
        if (response?.data?.success) {
          localStorage.setItem("currentCompleted", 1);
          localStorage.setItem("LPCurrentProvideruid", currentUserId);
          localStorage.setItem("LPCurrentClinicTitle", data.clinicName);
          localStorage.setItem("LPCurrentClinicId", response?.data?.data);
          goToStep(1);
          setClinicRegistrationData(apiData);
        } else {
          cogoToast.error(response?.data?.data);
        }

        // setClinicRegistrationData(apiData);
        // goToStep(1);
      } catch (error) {
        console.log(error)
      }
    } else {
      cogoToast.error(<div dangerouslySetInnerHTML={{ __html: `Create an account to onboard your clinic. Sign up <a href="/signup">here</a>!` }} />);
    }
  };

  return (
    <Box className={styles.formPageContainer}>
      <Typography variant="gotham2XL" className={styles.title}>
        {clinicRegistration.pageTitle}
      </Typography>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Stepper 
          orientation="vertical"
          sx={{
            '& .MuiStepConnector-root': { mb: '20px' },
          }}
        >
          {clinicRegistration.sections.map((section, index) => (
            <Step key={index} active>
              <StepLabel
                sx={{
                  alignItems: 'flex-start',
                  '& .MuiStepIcon-root': { color: 'white !important', border: '1px solid black', borderRadius: '50%', marginTop: '4px' },
                  '& .MuiStepIcon-text': { fill: 'black' },
                }}>
                <Typography variant="gothamLarge" className={`${styles.stepper} f-500`}>
                  {section.sectionName}
                </Typography>
                {section.introText && (
                  <Typography variant="gothamMedium" className={styles.stepper}>
                    {section.introText}
                  </Typography>
                )}
              </StepLabel>
              <StepContent>
                <Box>
                  {section.fields.map((field, fieldIndex) => {
                    if (field.group) {
                      return (
                        <Box key={fieldIndex} className={`${styles.stepper} ${styles.formFields} margin-top-15`}>
                          {field.fields.map((groupedField, groupedFieldIndex) => (
                            <Box key={groupedFieldIndex} style={{ flex: "1" }}>
                              <FormField 
                                field={{ ...groupedField, options }} register={register} 
                                onLinkButtonClick={(clickedField) =>
                                  handleLinkButtonClick(clickedField, groupedField, fieldIndex)
                                }
                                errors={errors}
                                control={control}
                              />
                            </Box>
                          ))}
                        </Box>
                      );
                    } else {
                      return (
                        <Box key={fieldIndex} className={`${styles.stepper} margin-top-15`}>
                          <FormField
                            field={field}
                            register={register}
                            onLinkButtonClick={(clickedField) =>
                              handleLinkButtonClick(clickedField, field, fieldIndex)
                            }
                            errors={errors}
                            selectedValues={selectedValues}
                            setSelectedValues={setSelectedValues}
                            control={control}
                          />
                        </Box>
                      );
                    }
                  })}
                </Box>
              </StepContent>
            </Step>
          ))}
        </Stepper>
        {
          Object.keys(errors).length > 0 && (
            <Box sx={{pl: '52px', mt: '12px'}}>
              {
                Object.entries(errors).map(([fieldId, error]) => (
                  <Box key={fieldId} sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
                    <ErrorIcon fontSize="small" color="error" />
                    <Typography variant="caption" color="error">
                      {error.message}
                    </Typography>
                  </Box>
                ))
              }
            </Box>
          )
        }
        <Box className={styles.submitButtonContainer}>
          {clinicRegistration.actions.map((action, actionIndex) => (
            <Button
              key={actionIndex}
              className={action.variant === 'primary' ? styles.primaryButton : styles.secondaryButton}
              size="large"
              type="submit"
            >
              {action.label}
            </Button>
          ))}
        </Box>
      </form>
    </Box >
  );
};

export default ClinicRegistration;
